"use client";

import { useEffect, useRef, useState } from "react";
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/16/solid";
import { CheckIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faFilePdf,
  faFileZipper,
} from "@fortawesome/free-regular-svg-icons";
import DownloadNotifications from "../Notifications/DownloadNotifications";

// Catalog of all possible export menu entries. Callers pick which subset to render
// via the `options` prop (array of ids). Defaults to the full post-completion menu
// so existing call sites that don't pass `options` keep the old behaviour.
export const EXPORT_OPTION_IDS = {
  EXCEL_PARTIAL: "EXCEL_PARTIAL",
  PDF_PARTIAL: "PDF_PARTIAL",
  EXCEL_RESPONSES: "EXCEL_RESPONSES",
  PDF_DOCS: "PDF_DOCS",
  EXCEL_FULL: "EXCEL_FULL",
  PDF_FULL: "PDF_FULL",
};

const DEFAULT_OPTIONS = [
  EXPORT_OPTION_IDS.EXCEL_RESPONSES,
  EXPORT_OPTION_IDS.PDF_DOCS,
  EXPORT_OPTION_IDS.EXCEL_FULL,
  EXPORT_OPTION_IDS.PDF_FULL,
];

export default function ExportMenu({
  exportExcel,
  exportDocs,
  exportPartial,
  notification,
  setNotification,
  fileType,
  details,
  downloadFileName,
  exportLogsId,
  options = DEFAULT_OPTIONS,
}) {
  const [downloadNotificationOpen, setDownloadNotificationOpen] =
    useState(false);

  // Tracks the in-flight export request so we can show a spinner between the click and the
  // moment the parent's DownloadNotifications takes over (signaled by `notification === true`).
  // Without this the user sees nothing happen for several seconds while the BE enqueues the job.
  const [isExporting, setIsExporting] = useState(false);
  const exportTimeoutRef = useRef(null);

  // Hand off to DownloadNotifications once it appears.
  useEffect(() => {
    if (notification && isExporting) {
      setIsExporting(false);
      if (exportTimeoutRef.current) {
        clearTimeout(exportTimeoutRef.current);
        exportTimeoutRef.current = null;
      }
    }
  }, [notification, isExporting]);

  useEffect(() => () => {
    if (exportTimeoutRef.current) clearTimeout(exportTimeoutRef.current);
  }, []);

  const runExport = (fn) => {
    if (isExporting) return;
    setIsExporting(true);
    // Hard cap the spinner — if the BE never calls back we still let the user retry.
    exportTimeoutRef.current = setTimeout(() => setIsExporting(false), 30000);
    try {
      fn();
    } catch (err) {
      setIsExporting(false);
    }
  };

  const ALL_ENTRIES = {
    [EXPORT_OPTION_IDS.EXCEL_PARTIAL]: {
      id: 5,
      name: "Excel - Partially complete",
      icon: (
        <FontAwesomeIcon
          icon={faFileExcel}
          className="w-[16px] h-[16px] text-green-700"
        />
      ),
      method: () => runExport(() => exportPartial && exportPartial("excel")),
    },
    [EXPORT_OPTION_IDS.PDF_PARTIAL]: {
      id: 6,
      name: "PDF - Partially complete",
      icon: (
        <FontAwesomeIcon
          icon={faFilePdf}
          className="w-[16px] h-[16px] text-red-700"
        />
      ),
      method: () => runExport(() => exportPartial && exportPartial("pdf")),
    },
    [EXPORT_OPTION_IDS.EXCEL_RESPONSES]: {
      id: 1,
      name: "Excel - Responses only",
      icon: (
        <FontAwesomeIcon
          icon={faFileExcel}
          className="w-[16px] h-[16px] text-green-700"
        />
      ),
      method: () => runExport(() => exportExcel("excel")),
    },
    [EXPORT_OPTION_IDS.PDF_DOCS]: {
      id: 2,
      name: "PDF - Responses only",
      icon: (
        <FontAwesomeIcon
          icon={faFilePdf}
          className="w-[16px] h-[16px] text-red-700"
        />
      ),
      method: () => runExport(() => exportExcel("pdf")),
    },
    [EXPORT_OPTION_IDS.EXCEL_FULL]: {
      id: 3,
      name: "Responses + Documents in Excel",
      icon: (
        <FontAwesomeIcon
          icon={faFileZipper}
          className="w-[16px] h-[16px] text-rose-700"
        />
      ),
      method: () => runExport(() => exportDocs("excel")),
    },
    [EXPORT_OPTION_IDS.PDF_FULL]: {
      id: 4,
      name: "Responses + Documents in PDF",
      icon: (
        <FontAwesomeIcon
          icon={faFileZipper}
          className="w-[16px] h-[16px] text-rose-700"
        />
      ),
      method: () => runExport(() => exportDocs("pdf")),
    },
  };

  const exportMenu = options
    .map((id) => ALL_ENTRIES[id])
    .filter(Boolean);
  const [selected, setSelected] = useState();
  // Selecting an option both records the choice and starts the export. Running the
  // export from onChange (not from a click handler inside the option) lets Headless UI
  // finish its own selection lifecycle first, which closes the options list. While the
  // list is open it is modal: the rest of the page is inert, so a notification's
  // Download button rendered underneath would swallow every click.
  const handleSelect = (item) => {
    setSelected(item);
    item?.method?.();
  };
  // NOTE: the Listbox itself must not flip to disabled while the selection click is
  // still being processed — Headless UI freezes mid-lifecycle and the options list
  // never closes. The ListboxButton alone is disabled during the export, and
  // runExport() already ignores re-entrant calls.
  return (
    <Listbox value={selected} onChange={handleSelect}>
      <div className="relative mt-2 w-80">
        {" "}
        {/* Increased width here */}
        <ListboxButton
          disabled={isExporting}
          className={`grid w-80 grid-cols-1 rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 ${
            isExporting ? "cursor-wait opacity-80" : "cursor-default"
          }`}
        >
          <span className="col-start-1 row-start-1 flex items-center gap-3 pr-6">
            {isExporting ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin text-indigo-600" />
                <span className="block truncate text-gray-700">
                  Preparing your export...
                </span>
              </>
            ) : selected ? (
              <>
                <span>{selected.icon}</span>
                <span className="block truncate">{selected.name}</span>
              </>
            ) : (
              <span className="text-gray-400">Select export option...</span>
            )}
          </span>
          <ChevronUpDownIcon
            aria-hidden="true"
            className={`col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4 ${
              isExporting ? "invisible" : ""
            }`}
          />
        </ListboxButton>
        <ListboxOptions
          transition
          modal={false}
          className="absolute z-10 mt-1 max-h-56 w-80 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
        >
          {exportMenu.map((item) => (
            <ListboxOption
              key={item.id}
              value={item}
              className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
            >
              <div className="flex items-center">
                <span className="group-data-[focus]:text-white">
                  {item.icon}
                </span>
                <span className="ml-3 block truncate font-normal group-data-[selected]:font-semibold">
                  {item.name}
                </span>
              </div>
              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[:not([data-selected])]:hidden group-data-[focus]:text-white">
                <CheckIcon aria-hidden="true" className="size-5" />
              </span>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>

      <DownloadNotifications
        show={notification}
        setShow={setNotification}
        fileType={fileType}
        id={details?.questionnaire_id}
        name={details?.name}
        downloadFileName={downloadFileName}
        exportLogsId={exportLogsId}
      />
    </Listbox>
  );
}
